import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Problem, Settings, DailySchedule as DailyScheduleType, Outcome, SessionResult } from '../types/problem';
import { fetchProblems, fetchSettings, updateSettings, fetchTodaySchedule, markRevised, ApiError } from '../hooks/useApi';
import { localDayStart } from '../utils/format';
import { useAuth } from '../auth';
import { Link, navigate } from '../router';
import DailySchedule from '../components/DailySchedule';
import SettingsPanel from '../components/SettingsPanel';
import Filters from '../components/Filters';
import RevisionCard from '../components/RevisionCard';
import Results from '../components/Results';
import Icon from '../components/Icon';
import { UserMenu } from '../components/Site';

export type Section = 'today' | 'practice' | 'settings';
type View = Section | 'revision' | 'results';

const NAV: { view: Section; label: string; path: string }[] = [
  { view: 'today', label: 'Today', path: '/app' },
  { view: 'practice', label: 'Practice', path: '/app/practice' },
  { view: 'settings', label: 'Settings', path: '/app/settings' },
];

function shuffle<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export default function Dashboard({ section }: { section: Section }) {
  const { me, refresh } = useAuth();
  const [problems, setProblems] = useState<Problem[]>([]);
  const [schedule, setSchedule] = useState<DailyScheduleType | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [session, setSession] = useState<'revision' | 'results' | null>(null);
  const view: View = session ?? section;
  const [queue, setQueue] = useState<Problem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<SessionResult[]>([]);

  const loadData = useCallback(async () => {
    try {
      const [probs, sched, setts] = await Promise.all([
        fetchProblems(),
        fetchTodaySchedule(),
        fetchSettings(),
      ]);
      setProblems(probs);
      setSchedule(sched);
      setSettings(setts);
      setError('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        await refresh();
        return;
      }
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [refresh]);

  useEffect(() => { loadData(); }, [loadData]);
  const setView = (v: Section) => {
    setSession(null);
    navigate(NAV.find(n => n.view === v)!.path);
  };

  const filtered = useMemo(() => {
    const from = dateFrom ? localDayStart(dateFrom) : -Infinity;
    const to = dateTo ? localDayStart(dateTo) + 86400000 : Infinity;
    return problems.filter(p => p.savedAt >= from && p.savedAt < to);
  }, [problems, dateFrom, dateTo]);

  function startSession(sessionProblems: Problem[]) {
    setQueue(shuffle(sessionProblems));
    setCurrentIndex(0);
    setResults([]);
    setSession('revision');
  }

  // Throws if the review can't be saved so the card can show the error and let the user retry.
  async function handleNext(outcome: Outcome) {
    const current = queue[currentIndex];
    if (outcome !== 'skipped') {
      await markRevised(current.slug, outcome === 'recalled');
    }
    setResults(prev => [...prev, { problem: current, outcome }]);

    if (currentIndex + 1 < queue.length) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setSession('results');
      refresh();
    }
  }

  function exitSession() {
    loadData();
    refresh();
    setView('today');
  }

  async function handleSaveSettings(newSettings: Settings) {
    const saved = await updateSettings(newSettings);
    setSettings(saved);
    setSchedule(await fetchTodaySchedule());
    setView('today');
  }

  const inSession = view === 'revision' || view === 'results';

  let content: React.ReactNode = null;
  if (loading) {
    content = (
      <div className="center-state">
        <div className="spinner spinner-lg" />
        <p>Loading your problems…</p>
      </div>
    );
  } else if (error) {
    content = (
      <div className="center-state">
        <div className="card">
          <h2>Can't reach the server</h2>
          <p>Check your connection and try again.</p>
          <button style={{ marginTop: 16 }} className="btn btn-primary btn-block" onClick={() => { setLoading(true); loadData(); }}>
            Retry
          </button>
        </div>
      </div>
    );
  } else if (view === 'today' && schedule && settings) {
    content = (
      <DailySchedule
        schedule={schedule}
        problems={problems}
        onStartDaily={() => startSession(schedule.problems)}
        onCustomSession={() => setView('practice')}
        onOpenSettings={() => setView('settings')}
      />
    );
  } else if (view === 'settings' && settings) {
    content = (
      <SettingsPanel
        settings={settings}
        problems={problems}
        onSave={handleSaveSettings}
        onImported={loadData}
      />
    );
  } else if (view === 'practice') {
    content = (
      <Filters
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        filteredCount={filtered.length}
        totalCount={problems.length}
        onStart={() => startSession(filtered)}
      />
    );
  } else if (view === 'revision' && queue[currentIndex]) {
    content = (
      <RevisionCard
        key={queue[currentIndex].id}
        problem={queue[currentIndex]}
        index={currentIndex}
        total={queue.length}
        maxInterval={settings?.revisionCycleDays ?? 14}
        onNext={handleNext}
      />
    );
  } else if (view === 'results') {
    content = <Results results={results} onDone={exitSession} />;
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/" className="brand" style={{ textDecoration: 'none' }} onClick={e => {
            if (inSession) { e.preventDefault(); exitSession(); }
          }}>
            <span className="brand-mark"><Icon name="logo" size={18} /></span>
            <span className="brand-name">CodeReviser</span>
          </Link>
          {inSession ? (
            <div className="session-bar">
              {view === 'revision' && (
                <span className="session-count">{currentIndex + 1} / {queue.length}</span>
              )}
              <button className="btn btn-secondary btn-sm" onClick={exitSession}>
                {view === 'results' ? 'Done' : 'End session'}
              </button>
            </div>
          ) : (
            <div className="session-bar">
              <nav className="nav" aria-label="Main">
                {NAV.map(item => (
                  <button
                    key={item.view}
                    className={`nav-item ${view === item.view ? 'active' : ''}`}
                    aria-current={view === item.view ? 'page' : undefined}
                    onClick={() => setView(item.view)}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
              {me?.user?.plan === 'free' && (
                <Link to="/pricing" className="btn btn-secondary btn-sm upgrade-btn">
                  <Icon name="star" size={14} /> Upgrade
                </Link>
              )}
              <UserMenu />
            </div>
          )}
        </div>
      </header>

      <main className="page">{content}</main>
    </div>
  );
}

